import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdAdd, MdSchedule, MdWarning, MdCheckCircle, MdEdit, MdClose, MdBuild, MdEmail, MdMeetingRoom, MdInventory, MdUploadFile, MdFileDownload } from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';

const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'quarterly', 'semi_annual', 'annual'];

async function copyText(text) {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch { /* Use the legacy browser fallback below. */ }
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (copied) return true;
    window.prompt(translateUiText('Copy this vendor link:'), text);
    return false;
}

// Mirrors the backend's _compute_end_date in routes/maintenance.py: the
// manager only ever picks a start date — the end date is always derived
// from it plus the chosen frequency, shown here for preview only.
const FREQUENCY_DAYS = { daily: 1, weekly: 7 };
const FREQUENCY_MONTHS = { monthly: 1, quarterly: 3, semi_annual: 6, annual: 12 };

function computeEndDate(startDateStr, frequency) {
    // Pure calendar-date math done entirely in UTC — parsing "YYYY-MM-DD" as
    // local time and reading it back via toISOString() shifts the date by a
    // day for any viewer not in UTC (Kigali is UTC+2), which would silently
    // disagree with the backend's timezone-free computation.
    if (!startDateStr) return '';
    const [y, m, day] = startDateStr.split('-').map(Number);
    if (!y || !m || !day) return '';
    const start = new Date(Date.UTC(y, m - 1, day));
    if (Number.isNaN(start.getTime())) return '';
    let end;
    if (FREQUENCY_DAYS[frequency]) {
        end = new Date(start.getTime() + FREQUENCY_DAYS[frequency] * 86400000);
    } else {
        const months = FREQUENCY_MONTHS[frequency] || 1;
        const targetMonthIndex = start.getUTCMonth() + months;
        const targetYear = start.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
        const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
        const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
        const targetDay = Math.min(day, daysInTargetMonth);
        end = new Date(Date.UTC(targetYear, targetMonth, targetDay));
    }
    return end.toISOString().slice(0, 10);
}

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const emptyScheduleForm = { asset_id: '', space_id: '', title: '', description: '', action_details: '', frequency: 'monthly', start_date: '', end_date: '', assigned_to: '', estimated_hours: '', notify_affected: true };
const emptyLogForm = { asset_id: '', description: '', findings: '', parts_used: '', cost: '', next_due: '', schedule_id: '', parts: [] };

function SpaceDetails({ item }) {
    if (!item?.space_name) return <span style={{ color: '#94a3b8' }}>General</span>;
    const location = [item.building_name, item.space_floor != null && `Floor ${item.space_floor}`, item.space_room_number && `Room ${item.space_room_number}`].filter(Boolean).join(' · ');
    const capacity = item.space_capacity ? `${item.space_capacity} people` : '';
    const area = item.space_area ? `${item.space_area} m²` : '';
    return <div style={{ minWidth: 180 }}>
        <div style={{ fontWeight: 600 }}>{item.space_name}</div>
        {location && <div style={{ fontSize: 11, color: '#64748b' }}>{location}</div>}
        {(capacity || area) && <div style={{ fontSize: 11, color: '#94a3b8' }}>{[capacity, area].filter(Boolean).join(' · ')}</div>}
        {item.space_amenities && <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.space_amenities}</div>}
    </div>;
}

export default function Maintenance() {
    const { isManager, assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const { t } = useLanguage();
    const [schedules, setSchedules] = useState([]);
    const [logs, setLogs] = useState([]);
    const [upcoming, setUpcoming] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [assets, setAssets] = useState([]);
    const [users, setUsers] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [affectedBookings, setAffectedBookings] = useState([]);
    const [checkingAffectedBookings, setCheckingAffectedBookings] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);
    const [logForm, setLogForm] = useState(emptyLogForm);
    const [editScheduleId, setEditScheduleId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('schedules');

    // Bulk import from a spreadsheet (like a "52 weeks maintenance plan"
    // document) — upload gets parsed server-side into a preview the manager
    // reviews before anything is actually created, same dry-run-then-confirm
    // shape used elsewhere in the app.
    const importFileRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importPreview, setImportPreview] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Planned parts for whichever schedule is open in the modal, and the
    // parts actually being consumed on the log being written — "where one
    // needs to be used, be ready": readiness comes from live stock, and
    // saving a log auto-deducts from stock server-side.
    const [inventoryItems, setInventoryItems] = useState([]);
    const [scheduleParts, setScheduleParts] = useState([]);
    const [newSchedulePart, setNewSchedulePart] = useState({ item_id: '', quantity_needed: '' });
    // Items picked while creating a brand-new schedule, before it has an id
    // to attach a real inventory_requirements row to — held here and sent
    // to the backend right after the schedule itself is created, so
    // "select an asset AND items" works in one pass instead of forcing a
    // save-then-add-parts round trip.
    const [pendingParts, setPendingParts] = useState([]);
    const [partsBusy, setPartsBusy] = useState(false);

    // Item/asset requests an assigned employee filed against whichever
    // schedule is open — the maintenance-side counterpart of Work Orders'
    // Asset Requests panel, same approve → vendor quote → PO flow.
    const [scheduleAssetRequests, setScheduleAssetRequests] = useState([]);
    const [selectedScheduleQuotations, setSelectedScheduleQuotations] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [schedulesRes, logsRes, upcomingRes, statsRes] = await Promise.all([
                API.get('/maintenance/schedules'),
                API.get('/maintenance/logs'),
                API.get('/maintenance/upcoming?days=30'),
                API.get('/maintenance/stats')
            ]);
            setSchedules(schedulesRes.data);
            setLogs(logsRes.data);
            setUpcoming(upcomingRes.data);
            setStats(statsRes.data);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        API.get('/assets').then(r => setAssets(r.data));
        API.get('/auth/users/list').then(r => setUsers(r.data.filter(u => u.role !== 'developer')));
        API.get('/spaces').then(r => setSpaces(r.data));
        API.get('/inventory').then(r => setInventoryItems(r.data));
    }, []);

    const loadScheduleParts = async scheduleId => {
        const { data } = await API.get('/inventory/requirements', { params: { maintenance_schedule_id: scheduleId } });
        setScheduleParts(data);
    };

    const loadScheduleAssetRequests = async scheduleId => {
        const { data } = await API.get('/asset-requests', { params: { maintenance_schedule_id: scheduleId } });
        setScheduleAssetRequests(data);
    };

    const openCreateSchedule = () => { setScheduleForm(emptyScheduleForm); setAffectedBookings([]); setScheduleParts([]); setScheduleAssetRequests([]); setPendingParts([]); setEditScheduleId(null); setShowScheduleModal(true); };
    const openEditSchedule = s => { setScheduleForm({ ...emptyScheduleForm, ...s }); setAffectedBookings([]); setEditScheduleId(s.id); setShowScheduleModal(true); loadScheduleParts(s.id); loadScheduleAssetRequests(s.id); };

    const addSchedulePart = async () => {
        if (!newSchedulePart.item_id || !newSchedulePart.quantity_needed) return;
        if (!editScheduleId) {
            // Not saved yet — just hold it locally until Save creates the
            // schedule and gives us a real id to attach it to.
            setPendingParts([...pendingParts, { ...newSchedulePart }]);
            setNewSchedulePart({ item_id: '', quantity_needed: '' });
            return;
        }
        setPartsBusy(true);
        try {
            await API.post('/inventory/requirements', {
                maintenance_schedule_id: editScheduleId, item_id: newSchedulePart.item_id, quantity_needed: newSchedulePart.quantity_needed
            });
            setNewSchedulePart({ item_id: '', quantity_needed: '' });
            await loadScheduleParts(editScheduleId);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not add part')); }
        finally { setPartsBusy(false); }
    };

    const removePendingPart = idx => setPendingParts(pendingParts.filter((_, i) => i !== idx));

    const removeSchedulePart = async reqId => {
        setPartsBusy(true);
        try {
            await API.delete(`/inventory/requirements/${reqId}`);
            await loadScheduleParts(editScheduleId);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not remove part')); }
        finally { setPartsBusy(false); }
    };

    const reviewScheduleAssetRequest = async (requestId, action) => {
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/${action}`);
            if (data.vendor_link) {
                const link = new URL(data.vendor_link, window.location.origin).href;
                const copied = await copyText(link);
                alert(copied ? `${translateUiText('Request approved. Vendor link copied:')}\n${link}` : `${translateUiText('Select and copy the vendor link:')}\n${link}`);
            }
            await loadScheduleAssetRequests(editScheduleId);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Request review failed')); }
    };

    const copyScheduleVendorLink = async requestId => {
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/vendor-link`);
            const link = new URL(data.vendor_link, window.location.origin).href;
            const copied = await copyText(link);
            alert(copied ? `${translateUiText('Vendor link copied:')}\n${link}` : `${translateUiText('Select and copy the vendor link:')}\n${link}`);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not create vendor link')); }
    };

    const reviewScheduleQuotation = async (requestId, action) => {
        const quotationId = selectedScheduleQuotations[requestId];
        if (!quotationId) {
            alert(translateUiText('Select a vendor quotation before approval.'));
            return;
        }
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/quotation/${action}`, { quotation_id: quotationId });
            if (data.purchase_order) alert(`${translateUiText('Quotation approved. Purchase order')} ${data.purchase_order} ${translateUiText('created.')}`);
            await loadScheduleAssetRequests(editScheduleId);
            await loadScheduleParts(editScheduleId);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Quotation review failed')); }
    };

    const checkAffectedBookings = async () => {
        if (!scheduleForm.space_id || !scheduleForm.start_date) return;
        setCheckingAffectedBookings(true);
        try {
            const { data } = await API.get('/maintenance/schedules/affected-bookings', { params: {
                space_id: scheduleForm.space_id,
                start_date: scheduleForm.start_date,
                end_date: scheduleForm.end_date || scheduleForm.start_date
            }});
            setAffectedBookings(data);
        } finally { setCheckingAffectedBookings(false); }
    };

    const handleSaveSchedule = async e => {
        e.preventDefault(); setSaving(true);
        try {
            if (editScheduleId) {
                await API.put(`/maintenance/schedules/${editScheduleId}`, scheduleForm);
                setShowScheduleModal(false); load();
            } else {
                // Save and stay open, "graduated" into edit mode on the new
                // schedule's id, so planned parts can be added right away
                // instead of forcing a separate re-open just for that.
                const { data } = await API.post('/maintenance/schedules', scheduleForm);
                for (const part of pendingParts) {
                    try {
                        await API.post('/inventory/requirements', {
                            maintenance_schedule_id: data.id, item_id: part.item_id, quantity_needed: part.quantity_needed
                        });
                    } catch (err) { alert(err.response?.data?.error || translateUiText('Could not add part')); }
                }
                setPendingParts([]);
                setEditScheduleId(data.id);
                setScheduleAssetRequests([]);
                await loadScheduleParts(data.id);
                load();
            }
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); } finally { setSaving(false); }
    };

    // A log's parts consumption can be blocked server-side (409) if stock is
    // short. Same pattern as work orders: show the shortage, let the tech
    // confirm and resubmit with confirm_shortage to log it anyway.
    const saveLogPayload = async payload => {
        try {
            await API.post('/maintenance/logs', payload);
        } catch (err) {
            const shortage = err.response?.data?.shortage;
            if (err.response?.status === 409 && shortage) {
                const lines = shortage.map(s => `• ${s.name}: need ${s.qty}, have ${s.available}`).join('\n');
                if (window.confirm(`${err.response.data.error}\n\n${lines}\n\nLog it anyway?`)) {
                    await saveLogPayload({ ...payload, confirm_shortage: true });
                    return;
                }
            }
            throw err;
        }
    };

    const handleSaveLog = async e => {
        e.preventDefault(); setSaving(true);
        try {
            await saveLogPayload(logForm);
            setShowLogModal(false); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); } finally { setSaving(false); }
    };

    const addLogPart = () => setLogForm({ ...logForm, parts: [...logForm.parts, { item_id: '', quantity: '' }] });
    const updateLogPart = (idx, field, value) => setLogForm({
        ...logForm, parts: logForm.parts.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    });
    const removeLogPart = idx => setLogForm({ ...logForm, parts: logForm.parts.filter((_, i) => i !== idx) });

    const handleDeleteSchedule = async schedule => {
        if (!window.confirm(`${translateUiText('Delete maintenance schedule')} "${schedule.title}"? ${translateUiText('Maintenance logs will be preserved.')}`)) return;
        try {
            await API.delete(`/maintenance/schedules/${schedule.id}`);
            load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Delete failed')); }
    };

    const handleImportFileChange = async e => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await API.post('/maintenance/schedules/import/preview', formData);
            setImportPreview(data);
            setShowImportModal(true);
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Could not read that file'));
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    };

    const confirmImport = async () => {
        if (!importPreview) return;
        setImporting(true);
        try {
            const validRows = importPreview.rows.filter(r => r.valid);
            const { data } = await API.post('/maintenance/schedules/import/commit', { rows: validRows });
            const failedCount = data.failed?.length || 0;
            alert(
                `${translateUiText('Imported')} ${data.created} ${data.created === 1 ? translateUiText('schedule') : translateUiText('schedules')}.` +
                (failedCount ? ` ${failedCount} ${translateUiText('row(s) could not be saved.')}` : '')
            );
            setShowImportModal(false);
            setImportPreview(null);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Import failed'));
        } finally {
            setImporting(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await API.get('/maintenance/schedules/export', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `maintenance_schedules_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(translateUiText('Export failed'));
        } finally {
            setExporting(false);
        }
    };

    const openLogForSchedule = async schedule => {
        setLogForm({ ...emptyLogForm, asset_id: schedule.asset_id || '', schedule_id: schedule.id });
        setShowLogModal(true);
        // Prefill with this schedule's planned parts (still editable) so the
        // tech isn't retyping what was already planned for the visit.
        try {
            const { data } = await API.get('/inventory/requirements', { params: { maintenance_schedule_id: schedule.id } });
            const pending = data.filter(p => !p.fulfilled);
            if (pending.length) {
                setLogForm(current => ({ ...current, parts: pending.map(p => ({ item_id: String(p.item_id), quantity: String(p.quantity_needed) })) }));
            }
        } catch { /* planned parts are a convenience prefill; log still works without them */ }
    };

    // One-click alternative to the full "Log Maintenance Work" modal: for the
    // common case (nothing to note, no parts consumed) this logs it done today
    // and, by leaving next_due blank, lets the backend auto-advance the
    // schedule on its own frequency — clearing "overdue" without breaking the
    // recurring cycle.
    const markScheduleDone = async schedule => {
        if (!window.confirm(translateUiText(
            `Mark "${schedule.title}" as done today? This logs it and auto-schedules the next ${schedule.frequency?.replace('_', ' ') || ''} due date.`
        ))) return;
        try {
            await saveLogPayload({ schedule_id: schedule.id, asset_id: schedule.asset_id || '', description: 'Marked done' });
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Save failed'));
        }
    };

    const dueStatus = dateStr => {
        if (!dateStr) return { color: '#94a3b8', label: 'No date' };
        const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
        if (days < 0) return { color: '#b91c1c', label: `${Math.abs(days)}d overdue` };
        if (days === 0) return { color: '#b45309', label: 'Due today' };
        if (days <= 7) return { color: '#b45309', label: `${days}d left` };
        return { color: '#15803d', label: `${days}d left` };
    };

    const freqBadgeColor = { daily: '#fee2e2', weekly: '#fef3c7', monthly: '#e0f2fe', quarterly: '#dcfce7', semi_annual: '#f3e8ff', annual: '#f1f5f9' };
    const freqTextColor = { daily: '#b91c1c', weekly: '#b45309', monthly: '#0369a1', quarterly: '#15803d', semi_annual: '#9333ea', annual: '#475569' };
    const tabLabels = { schedules: t('maintenance.tabSchedules', 'Schedules'), upcoming: t('maintenance.tabUpcoming', 'Upcoming'), logs: t('maintenance.tabLogs', 'Logs') };
    const freqLabels = { daily: t('maintenance.freqDaily', 'daily'), weekly: t('maintenance.freqWeekly', 'weekly'), monthly: t('maintenance.freqMonthly', 'monthly'), quarterly: t('maintenance.freqQuarterly', 'quarterly'), semi_annual: t('maintenance.freqSemiAnnual', 'semi-annual'), annual: t('maintenance.freqAnnual', 'annual') };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('maintenance.pageTitle', 'Maintenance Management')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager && assignedFacility
                            ? `${assignedFacility.name} ${t('maintenance.pageSubtitle', '— preventive & corrective maintenance')}`
                            : t('maintenance.pageSubtitleShort', 'Preventive & corrective maintenance schedules')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input
                        ref={importFileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        onChange={handleImportFileChange}
                    />
                    <button className="btn btn-light btn-sm" onClick={() => importFileRef.current?.click()} disabled={importing} style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={t('maintenance.importTooltip', 'Import schedules from a spreadsheet (.xlsx, .xls, .csv)')}>
                        <MdUploadFile size={16} /> {importing ? t('maintenance.importing', 'Reading...') : t('maintenance.importButton', 'Import')}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={handleExport} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={t('maintenance.exportTooltip', 'Export current schedules to a spreadsheet')}>
                        <MdFileDownload size={16} /> {exporting ? t('maintenance.exporting', 'Exporting...') : t('maintenance.exportButton', 'Export')}
                    </button>
                    <button className="btn btn-light btn-sm" onClick={() => { setLogForm(emptyLogForm); setShowLogModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdBuild size={16} /> {t('maintenance.logWorkButton', 'Log Work')}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={openCreateSchedule} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdAdd size={16} /> {t('maintenance.newScheduleButton', 'New Schedule')}
                    </button>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="row g-3 mb-4">
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon red"><MdWarning /></div>
                            <div><div className="stat-value">{stats.overdue}</div><div className="stat-label">{t('maintenance.statOverdue', 'Overdue')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon yellow"><MdSchedule /></div>
                            <div><div className="stat-value">{stats.due_this_month}</div><div className="stat-label">{t('maintenance.statDueThisMonth', 'Due This Month')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon blue"><MdSchedule /></div>
                            <div><div className="stat-value">{schedules.length}</div><div className="stat-label">{t('maintenance.statTotalSchedules', 'Total Schedules')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon green"><MdCheckCircle /></div>
                            <div><div className="stat-value">{logs.length}</div><div className="stat-label">{t('maintenance.statTotalLogs', 'Total Logs')}</div></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                {['schedules', 'upcoming', 'logs'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{
                        padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                        fontWeight: 500, fontSize: 14, textTransform: 'capitalize',
                        color: activeTab === tab ? '#0ea5e9' : '#64748b',
                        borderBottom: activeTab === tab ? '2px solid #0ea5e9' : '2px solid transparent',
                    }}>{tabLabels[tab]}</button>
                ))}
            </div>

            {loading ? (
                <div className="spinner-wrap"><div className="spinner-border" /></div>
            ) : (
                <>
                    {/* Schedules Tab */}
                    {activeTab === 'schedules' && (
                        <div className="page-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="fms-table">
                                    <thead>
                                        <tr><th>{t('maintenance.colTitle', 'Title')}</th><th>{t('maintenance.colSpace', 'Maintained Space')}</th><th>{t('maintenance.colAsset', 'Asset')}</th><th>{t('maintenance.colFrequency', 'Frequency')}</th><th>{t('maintenance.colStartDate', 'Starting Date')}</th><th>{t('maintenance.colEndDate', 'Ending Date')}</th><th>{t('maintenance.colAssigned', 'Assigned')}</th><th>{t('maintenance.colLastPerformed', 'Last Performed')}</th><th>{t('maintenance.colParts', 'Parts')}</th><th>{t('maintenance.colActions', 'Actions')}</th></tr>
                                    </thead>
                                    <tbody>
                                        {schedules.map(s => {
                                            const due = dueStatus(s.next_due);
                                            return (
                                                <tr key={s.id}>
                                                    <td style={{ fontWeight: 500 }}>{s.title}</td>
                                                    <td><SpaceDetails item={s} /></td>
                                                    <td style={{ fontSize: 13 }}>{s.asset_name || s.space_name || <span style={{ color: '#94a3b8' }}>General</span>}</td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: 12, padding: '2px 8px', borderRadius: 12, fontWeight: 500,
                                                            background: freqBadgeColor[s.frequency] || '#f1f5f9',
                                                            color: freqTextColor[s.frequency] || '#475569'
                                                        }}>
                                                            {s.frequency?.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td><div style={{ fontWeight: 600, fontSize: 13 }}>{s.start_date || s.next_due}</div></td>
                                                    <td><div style={{ fontWeight: 600, fontSize: 13 }}>{s.end_date || '—'}</div><div style={{ fontSize: 11, color: due.color }}>{due.label}</div></td>
                                                    <td style={{ fontSize: 13 }}>{s.assigned_user || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{s.last_performed || '—'}</td>
                                                    <td>
                                                        {s.parts_total > 0 ? (
                                                            s.parts_ready ? (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d' }}>
                                                                    <MdCheckCircle size={12} /> {t('maintenance.partReady', 'Ready')}
                                                                </span>
                                                            ) : (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#fee2e2', color: '#b91c1c' }}>
                                                                    <MdWarning size={12} /> {t('maintenance.partShort', 'Short')}
                                                                </span>
                                                            )
                                                        ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: 6 }}>
                                                            <button className="btn btn-sm btn-light" onClick={() => markScheduleDone(s)} title={t('maintenance.markDoneTooltip', 'Mark done (logs it and auto-schedules the next due date)')}>
                                                                <MdCheckCircle size={14} />
                                                            </button>
                                                            <button className="btn btn-sm btn-light" onClick={() => openLogForSchedule(s)} title={t('maintenance.logTooltip', 'Log maintenance (add notes, parts, cost)')}>
                                                                <MdBuild size={14} />
                                                            </button>
                                                            <button className="btn btn-sm btn-light" onClick={() => openEditSchedule(s)} title={t('maintenance.edit', 'Edit')}>
                                                                <MdEdit size={14} />
                                                            </button>
                                                            <button className="btn btn-sm btn-light" style={{ color: '#ef4444' }} onClick={() => handleDeleteSchedule(s)} title={t('maintenance.delete', 'Delete')}>
                                                                <MdClose size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {!schedules.length && (
                                            <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('maintenance.noSchedules', 'No schedules yet')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Upcoming Tab */}
                    {activeTab === 'upcoming' && (
                        <div className="row g-3">
                            {upcoming.map(item => (
                                <div key={item.id} className="col-lg-4 col-md-6">
                                    <div style={{
                                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
                                        borderLeft: `4px solid ${item.days_until_due <= 0 ? '#ef4444' : item.days_until_due <= 7 ? '#f59e0b' : '#0ea5e9'}`
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600, fontSize: 14 }}>{item.title}</span>
                                            <span style={{
                                                fontSize: 12, fontWeight: 700,
                                                color: item.days_until_due <= 0 ? '#b91c1c' : item.days_until_due <= 7 ? '#b45309' : '#15803d'
                                            }}>
                                                {item.days_until_due <= 0 ? `${Math.abs(item.days_until_due)}d overdue` : `${item.days_until_due}d`}
                                            </span>
                                        </div>
                                        {item.asset_name && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{item.asset_name} ({item.asset_tag})</div>}
                                        {item.space_name && <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', margin: '8px 0', fontSize: 12 }}><SpaceDetails item={item} /></div>}
                                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('maintenance.startingLabel', 'Starting: ')}{item.start_date || item.next_due}</div>
                                        {item.end_date && <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('maintenance.endingLabel', 'Ending: ')}{item.end_date}</div>}
                                        {item.assigned_user && <div style={{ fontSize: 12, color: '#0ea5e9', marginTop: 4 }}>{t('maintenance.assignedLabel', 'Assigned: ')}{item.assigned_user}</div>}
                                    </div>
                                </div>
                            ))}
                            {!upcoming.length && (
                                <div className="col-12" style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                                    <MdCheckCircle size={48} style={{ color: '#22c55e', marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
                                    {t('maintenance.noUpcoming', 'No upcoming maintenance in the next 30 days')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Logs Tab */}
                    {activeTab === 'logs' && (
                        <div className="page-card">
                            <div style={{ overflowX: 'auto' }}>
                                <table className="fms-table">
                                    <thead>
                                        <tr><th>{t('maintenance.logColDate', 'Date')}</th><th>{t('maintenance.logColSpace', 'Maintained Space')}</th><th>{t('maintenance.logColAsset', 'Asset')}</th><th>{t('maintenance.logColDescription', 'Description')}</th><th>{t('maintenance.logColFindings', 'Findings')}</th><th>{t('maintenance.logColPartsUsed', 'Parts Used')}</th><th>{t('maintenance.logColCost', 'Cost')}</th><th>{t('maintenance.logColPerformedBy', 'Performed By')}</th></tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(l => (
                                            <tr key={l.id}>
                                                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(l.performed_at).toLocaleDateString()}</td>
                                                <td><SpaceDetails item={l} /></td>
                                                <td style={{ fontSize: 13 }}>{l.asset_name || '—'}</td>
                                                <td style={{ maxWidth: 200 }}>{l.description || '—'}</td>
                                                <td style={{ maxWidth: 200, fontSize: 12, color: '#64748b' }}>{l.findings || '—'}</td>
                                                <td style={{ fontSize: 12 }}>{l.parts_used || '—'}</td>
                                                <td style={{ fontSize: 13 }}>{l.cost ? `${currency} ${Number(l.cost).toLocaleString()}` : '—'}</td>
                                                <td style={{ fontSize: 13 }}>{l.performed_by_name || '—'}</td>
                                            </tr>
                                        ))}
                                        {!logs.length && (
                                            <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>{t('maintenance.noLogs', 'No maintenance logs yet')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Schedule Modal */}
            {showScheduleModal && (
                <Modal title={editScheduleId ? t('maintenance.editScheduleModalTitle', 'Edit Schedule') : t('maintenance.newScheduleModalTitle', 'New Maintenance Schedule')} onClose={() => setShowScheduleModal(false)}>
                    <form onSubmit={handleSaveSchedule}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('maintenance.labelTitle', 'Title *')}</label>
                                <input className="form-control" value={scheduleForm.title} onChange={e => setScheduleForm({ ...scheduleForm, title: e.target.value })} required placeholder={t('maintenance.scheduleTitlePlaceholder', 'e.g. Monthly HVAC Filter Replacement')} />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('maintenance.labelDescription', 'Description')}</label>
                                <textarea className="form-control" rows={2} value={scheduleForm.description} onChange={e => setScheduleForm({ ...scheduleForm, description: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('maintenance.labelAction', 'Maintenance Action')}</label>
                                <textarea className="form-control" rows={2} value={scheduleForm.action_details} onChange={e => setScheduleForm({ ...scheduleForm, action_details: e.target.value })} placeholder={t('maintenance.actionPlaceholder', 'Explain what work will take place and how the space may be affected')} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelAsset', 'Asset')}</label>
                                <select className="form-select" value={scheduleForm.asset_id} onChange={e => setScheduleForm({ ...scheduleForm, asset_id: e.target.value })}>
                                    <option value="">{t('maintenance.noSpecificAsset', 'No specific asset')}</option>
                                    {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelAffectedSpace', 'Affected Space')}</label>
                                <select className="form-select" value={scheduleForm.space_id} onChange={e => { setScheduleForm({ ...scheduleForm, space_id: e.target.value }); setAffectedBookings([]); }}>
                                    <option value="">{t('maintenance.noSpecificSpace', 'No specific space')}</option>
                                    {spaces.map(space => <option key={space.id} value={space.id}>{space.name}{space.building_name ? ` - ${space.building_name}` : ''}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelFrequency', 'Frequency')}</label>
                                <select className="form-select" value={scheduleForm.frequency} onChange={e => setScheduleForm({ ...scheduleForm, frequency: e.target.value, end_date: computeEndDate(scheduleForm.start_date || scheduleForm.next_due, e.target.value) })}>
                                    {FREQUENCY_OPTIONS.map(f => <option key={f} value={f}>{freqLabels[f]}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelStartDate', 'Starting Date *')}</label>
                                <input type="date" className="form-control" value={scheduleForm.start_date || scheduleForm.next_due || ''} onChange={e => setScheduleForm({ ...scheduleForm, start_date: e.target.value, end_date: computeEndDate(e.target.value, scheduleForm.frequency) })} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelEndDate', 'Ending Date')}</label>
                                <input type="date" className="form-control" value={scheduleForm.end_date || computeEndDate(scheduleForm.start_date || scheduleForm.next_due, scheduleForm.frequency) || ''} disabled readOnly style={{ background: '#f1f5f9', color: '#64748b' }} />
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('maintenance.endDateHint', 'Auto-calculated from the start date and frequency')}</div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelAssignedTo', 'Assigned To')}</label>
                                <select className="form-select" value={scheduleForm.assigned_to} onChange={e => setScheduleForm({ ...scheduleForm, assigned_to: e.target.value })}>
                                    <option value="">{t('maintenance.unassigned', 'Unassigned')}</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.labelEstimatedHours', 'Estimated Hours')}</label>
                                <input type="number" className="form-control" step="0.5" value={scheduleForm.estimated_hours} onChange={e => setScheduleForm({ ...scheduleForm, estimated_hours: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, color: '#1e3a5f' }}>
                                            <input type="checkbox" checked={scheduleForm.notify_affected} onChange={e => setScheduleForm({ ...scheduleForm, notify_affected: e.target.checked })} />
                                            <MdEmail /> {t('maintenance.notifyAffectedLabel', 'Email affected confirmed bookings and rentals before this action')}
                                        </label>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={checkAffectedBookings} disabled={!scheduleForm.space_id || !scheduleForm.start_date || checkingAffectedBookings}>
                                            <MdMeetingRoom /> {checkingAffectedBookings ? t('maintenance.checking', 'Checking...') : t('maintenance.previewAffected', 'Preview affected people')}
                                        </button>
                                    </div>
                                    {affectedBookings.length > 0 && <div style={{ marginTop: 10, fontSize: 13, color: '#334155' }}>
                                        <strong>{affectedBookings.length} {t('maintenance.affectedCountLabel', 'confirmed booking(s) or rental(s) will be notified:')}</strong>
                                        {affectedBookings.map(booking => <div key={booking.id} style={{ marginTop: 8, padding: '8px 10px', background: '#fff', borderRadius: 6, border: '1px solid #dbeafe' }}>
                                            <strong>{booking.guest_name || booking.account_name || booking.title || 'Guest'}</strong> · {booking.order_type === 'rental' ? t('maintenance.rental', 'Rental') : t('maintenance.booking', 'Booking')}<br />
                                            <span style={{ fontSize: 12 }}>{booking.guest_email || booking.account_email || t('maintenance.noEmail', 'No email address')} · {new Date(booking.start_datetime).toLocaleString()} – {new Date(booking.end_datetime).toLocaleString()}</span>
                                        </div>)}
                                    </div>}
                                    {affectedBookings.length === 0 && scheduleForm.space_id && scheduleForm.start_date && <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>{t('maintenance.previewHint', 'Preview the overlap before saving. Only confirmed bookings and active rentals receive email notices.')}</div>}
                                </div>
                            </div>
                            <div className="col-12">
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                                    <label className="form-label fw-semibold" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MdInventory /> {t('maintenance.plannedPartsLabel', 'Planned Parts')}</label>
                                    {!editScheduleId && <>
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{t('maintenance.plannedPartsHint', "Pick any items this schedule will need — they're attached as soon as you save.")}</div>
                                        {pendingParts.map((p, idx) => {
                                            const item = inventoryItems.find(i => String(i.id) === String(p.item_id));
                                            return (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fff', padding: '8px 10px', marginBottom: 6, borderRadius: 6, fontSize: 12, border: '1px solid #e2e8f0' }}>
                                                    <div><strong>{item?.name || 'Item'}</strong> {item?.item_code && <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{item.item_code}</span>}<div style={{ color: '#64748b' }}>Qty {p.quantity_needed}{item?.unit ? ` ${item.unit}` : ''}</div></div>
                                                    <button type="button" className="btn btn-sm btn-light" onClick={() => removePendingPart(idx)} title="Remove"><MdClose size={13} /></button>
                                                </div>
                                            );
                                        })}
                                        {!pendingParts.length && <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{t('maintenance.noPartsPlanned', 'No parts planned yet.')}</div>}
                                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                            <select className="form-select form-select-sm" style={{ flex: 1 }} value={newSchedulePart.item_id} onChange={e => setNewSchedulePart({ ...newSchedulePart, item_id: e.target.value })}>
                                                <option value="">{t('maintenance.selectPart', 'Select a part...')}</option>
                                                {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                                            </select>
                                            <input type="number" className="form-control form-control-sm" style={{ width: 90 }} min={0.01} step="0.01" placeholder="Qty" value={newSchedulePart.quantity_needed} onChange={e => setNewSchedulePart({ ...newSchedulePart, quantity_needed: e.target.value })} />
                                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={!newSchedulePart.item_id || !newSchedulePart.quantity_needed} onClick={addSchedulePart}>{t('maintenance.addPartButton', 'Add')}</button>
                                        </div>
                                    </>}
                                    {editScheduleId && <>
                                        {scheduleParts.map(p => (
                                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fff', padding: '8px 10px', marginBottom: 6, borderRadius: 6, fontSize: 12, border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <strong>{p.item_name}</strong> <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{p.item_code}</span>
                                                    <div style={{ color: '#64748b' }}>Need {p.quantity_needed}{p.unit ? ` ${p.unit}` : ''} · Have {p.available}{p.unit ? ` ${p.unit}` : ''}</div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {p.fulfilled ? (
                                                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#e0f2fe', color: '#0369a1' }}>{t('maintenance.partUsed', 'Used')}</span>
                                                    ) : p.ready ? (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d' }}><MdCheckCircle size={12} /> {t('maintenance.partReady', 'Ready')}</span>
                                                    ) : (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#fee2e2', color: '#b91c1c' }}><MdWarning size={12} /> {t('maintenance.partShort', 'Short')}</span>
                                                    )}
                                                    {!p.fulfilled && (
                                                        <button type="button" className="btn btn-sm btn-light" disabled={partsBusy} onClick={() => removeSchedulePart(p.id)} title={t('maintenance.removeFromPlan', 'Remove from plan')}><MdClose size={13} /></button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {!scheduleParts.length && <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{t('maintenance.noPartsPlanned', 'No parts planned yet.')}</div>}
                                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                            <select className="form-select form-select-sm" style={{ flex: 1 }} value={newSchedulePart.item_id} onChange={e => setNewSchedulePart({ ...newSchedulePart, item_id: e.target.value })}>
                                                <option value="">{t('maintenance.selectPart', 'Select a part...')}</option>
                                                {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                                            </select>
                                            <input type="number" className="form-control form-control-sm" style={{ width: 90 }} min={0.01} step="0.01" placeholder="Qty" value={newSchedulePart.quantity_needed} onChange={e => setNewSchedulePart({ ...newSchedulePart, quantity_needed: e.target.value })} />
                                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={partsBusy || !newSchedulePart.item_id || !newSchedulePart.quantity_needed} onClick={addSchedulePart}>{t('maintenance.addPartButton', 'Add')}</button>
                                        </div>
                                    </>}
                                </div>
                            </div>
                            {editScheduleId && (
                                <div className="col-12">
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                                        <label className="form-label fw-semibold" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{t('maintenance.assetRequestsLabel', 'Item/Asset Requests ({n})', { n: scheduleAssetRequests.length })}</label>
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{t('maintenance.assetRequestsHint', "Requests the assigned employee filed for something not in stock. Approving and getting a PO approved auto-adds it here — an item restocks and joins Planned Parts above, a new asset attaches to this schedule.")}</div>
                                        {scheduleAssetRequests.map(assetRequest => <div key={assetRequest.id} style={{ background: '#fff', border: '1px solid #e2e8f0', padding: 10, marginBottom: 8, fontSize: 12, borderRadius: 6 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                                <strong>{assetRequest.request_type === 'inventory_item' ? '📦 ' : '🆕 '}{assetRequest.asset_name} × {assetRequest.quantity}</strong>
                                                <span className={`badge-status ${assetRequest.status}`}>{assetRequest.status.replaceAll('_', ' ')}</span>
                                            </div>
                                            <div style={{ color: '#64748b' }}>{t('maintenance.requestedBy', 'Requested by {name} ·', { name: assetRequest.requester_name || 'user' })} {assetRequest.request_type === 'inventory_item' ? t('maintenance.restockItem', 'Restock inventory item') : t('maintenance.newAsset', 'New asset')}</div>
                                            {(assetRequest.fulfilled_asset_id || assetRequest.fulfilled_requirement_id) && (
                                                <div style={{ color: '#15803d', fontWeight: 600, marginTop: 2 }}>✓ {t('maintenance.autoAddedAfterPO', 'Auto-added to this schedule after PO approval')}</div>
                                            )}
                                            {assetRequest.justification && <div>{assetRequest.justification}</div>}
                                            {assetRequest.quotation_amount != null && <div style={{ marginTop: 4 }}><strong>Vendor:</strong> {assetRequest.vendor_name} ({assetRequest.vendor_email}) · <strong>Quotation:</strong> {assetRequest.quotation_currency} {assetRequest.quotation_amount} · Delivery: {assetRequest.delivery_time}{assetRequest.quotation_notes ? ` · ${assetRequest.quotation_notes}` : ''}</div>}
                                            {assetRequest.vendor_quotations?.length > 0 && <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 6 }}>
                                                <strong>{t('maintenance.vendorSubmissions', 'Vendor submissions ({n})', { n: assetRequest.vendor_quotations.length })}</strong>
                                                {assetRequest.vendor_quotations.map(quotation => <label key={quotation.id} style={{ display: 'block', marginTop: 5, padding: 6, background: '#f8fafc', border: `1px solid ${selectedScheduleQuotations[assetRequest.id] === quotation.id ? '#0d6efd' : '#e2e8f0'}`, cursor: quotation.status === 'submitted' ? 'pointer' : 'default' }}>
                                                    <input type="radio" name={`schedule-quotation-${assetRequest.id}`} className="me-2" value={quotation.id} checked={selectedScheduleQuotations[assetRequest.id] === quotation.id} disabled={quotation.status !== 'submitted'} onChange={() => setSelectedScheduleQuotations(current => ({ ...current, [assetRequest.id]: quotation.id }))} />
                                                    <strong>{quotation.vendor_name}</strong> ({quotation.vendor_email}) · {quotation.currency} {quotation.amount} · {quotation.delivery_time}
                                                    <span className={`badge-status ${quotation.status}`} style={{ marginLeft: 6 }}>{quotation.status}</span>
                                                    {quotation.notes && <div>{quotation.notes}</div>}
                                                </label>)}
                                            </div>}
                                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                {assetRequest.status === 'pending' && <><button type="button" className="btn btn-sm btn-primary" onClick={() => reviewScheduleAssetRequest(assetRequest.id, 'approve')}>{t('maintenance.approveCreateLink', 'Approve & create vendor link')}</button><button type="button" className="btn btn-sm btn-light" onClick={() => reviewScheduleAssetRequest(assetRequest.id, 'reject')}>{t('maintenance.reject', 'Reject')}</button></>}
                                                {['approved', 'quotation_submitted', 'quotation_approved', 'quotation_rejected'].includes(assetRequest.status) && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => copyScheduleVendorLink(assetRequest.id)}>{t('maintenance.copyVendorLink', 'Copy vendor link')}</button>}
                                                {assetRequest.status === 'quotation_submitted' && <><button type="button" className="btn btn-sm btn-primary" onClick={() => reviewScheduleQuotation(assetRequest.id, 'approve')}>{t('maintenance.approveQuotation', 'Approve selected quotation')}</button><button type="button" className="btn btn-sm btn-light" onClick={() => reviewScheduleQuotation(assetRequest.id, 'reject')}>{t('maintenance.rejectQuotation', 'Reject selected quotation')}</button></>}
                                            </div>
                                        </div>)}
                                        {!scheduleAssetRequests.length && <div style={{ color: '#94a3b8', fontSize: 12 }}>{t('maintenance.noAssetRequests', 'No item or asset requests for this schedule.')}</div>}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowScheduleModal(false)}>{t('maintenance.cancel', 'Cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('maintenance.saving', 'Saving...')}</> : editScheduleId ? t('maintenance.saveSchedule', 'Save Schedule') : t('maintenance.createAddParts', 'Create & Add Parts')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Log Modal */}
            {showLogModal && (
                <Modal title={t('maintenance.logModalTitle', 'Log Maintenance Work')} onClose={() => setShowLogModal(false)}>
                    <form onSubmit={handleSaveLog}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelAsset', 'Asset')}</label>
                                <select className="form-select" value={logForm.asset_id} onChange={e => setLogForm({ ...logForm, asset_id: e.target.value })}>
                                    <option value="">{t('maintenance.selectAsset', 'Select asset')}</option>
                                    {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelSchedule', 'Schedule (if applicable)')}</label>
                                <select className="form-select" value={logForm.schedule_id} onChange={e => setLogForm({ ...logForm, schedule_id: e.target.value })}>
                                    <option value="">{t('maintenance.none', 'None')}</option>
                                    {schedules.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelDescription', 'Work Description')}</label>
                                <textarea className="form-control" rows={2} value={logForm.description} onChange={e => setLogForm({ ...logForm, description: e.target.value })} placeholder={t('maintenance.workDonePlaceholder', 'What was done...')} />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelFindings', 'Findings / Observations')}</label>
                                <textarea className="form-control" rows={2} value={logForm.findings} onChange={e => setLogForm({ ...logForm, findings: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
                                    <label className="form-label fw-semibold" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MdInventory /> {t('maintenance.logLabelPartsUsed', 'Parts Used')}</label>
                                    {logForm.parts.map((p, idx) => {
                                        const item = inventoryItems.find(i => String(i.id) === String(p.item_id));
                                        const available = item ? Number(item.quantity_in_stock) : null;
                                        const short = available != null && Number(p.quantity) > available;
                                        return (
                                            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                                <select className="form-select form-select-sm" style={{ flex: 1 }} value={p.item_id} onChange={e => updateLogPart(idx, 'item_id', e.target.value)}>
                                                    <option value="">{t('maintenance.selectPart', 'Select a part...')}</option>
                                                    {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                                                </select>
                                                <input type="number" className="form-control form-control-sm" style={{ width: 90 }} min={0.01} step="0.01" placeholder="Qty" value={p.quantity} onChange={e => updateLogPart(idx, 'quantity', e.target.value)} />
                                                {short && <span title={t('maintenance.notEnoughStock', 'Not enough in stock')} style={{ display: 'inline-flex', alignItems: 'center' }}><MdWarning size={16} color="#b91c1c" /></span>}
                                                <button type="button" className="btn btn-sm btn-light" onClick={() => removeLogPart(idx)}><MdClose size={13} /></button>
                                            </div>
                                        );
                                    })}
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLogPart}>{t('maintenance.addPartUsedButton', '+ Add part used')}</button>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>{t('maintenance.partsDeductionHint', "Each part picked here is deducted from stock when you save — it's blocked if stock is short unless you confirm.")}</div>
                                    <div style={{ marginTop: 10 }}>
                                        <label className="form-label" style={{ fontSize: 12 }}>{t('maintenance.notesLabel', 'Notes (only if not picking from inventory above)')}</label>
                                        <input className="form-control form-control-sm" value={logForm.parts_used} onChange={e => setLogForm({ ...logForm, parts_used: e.target.value })} placeholder={t('maintenance.partsNotesPlaceholder', 'e.g. Filter #3M-500 (not in inventory)')} />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelCost', 'Cost ({currency})', { currency })}</label>
                                <input type="number" className="form-control" step="0.01" value={logForm.cost} onChange={e => setLogForm({ ...logForm, cost: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('maintenance.logLabelNextDue', 'Next Service Due (optional)')}</label>
                                <input type="date" className="form-control" value={logForm.next_due} onChange={e => setLogForm({ ...logForm, next_due: e.target.value })} />
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('maintenance.nextDueHint', "Leave blank to auto-continue on the schedule's own frequency from today")}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowLogModal(false)}>{t('maintenance.cancel', 'Cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('maintenance.savingLog', 'Saving...')}</> : t('maintenance.saveLog', 'Save Log')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Import Preview Modal */}
            {showImportModal && importPreview && (
                <Modal title={t('maintenance.importModalTitle', 'Import Maintenance Schedules')} onClose={() => { setShowImportModal(false); setImportPreview(null); }}>
                    <div style={{ marginBottom: 12, fontSize: 13 }}>
                        <strong>{importPreview.valid_count}</strong> {t('maintenance.readyToImport', 'ready to import')}
                        {importPreview.invalid_count > 0 && <>, <strong style={{ color: '#b91c1c' }}>{importPreview.invalid_count}</strong> {t('maintenance.rowsSkipped', 'row(s) will be skipped (missing title or a readable date)')}</>}
                        .
                    </div>
                    <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <table className="fms-table">
                            <thead>
                                <tr><th>{t('maintenance.importColRow', 'Row')}</th><th>{t('maintenance.importColTitle', 'Title')}</th><th>{t('maintenance.importColFrequency', 'Frequency')}</th><th>{t('maintenance.importColStartDate', 'Start Date')}</th><th>{t('maintenance.importColAssigned', 'Assigned')}</th><th>{t('maintenance.importColNotes', 'Notes')}</th></tr>
                            </thead>
                            <tbody>
                                {importPreview.rows.map(r => (
                                    <tr key={r.row} style={{ opacity: r.valid ? 1 : 0.5 }}>
                                        <td style={{ fontSize: 12, color: '#94a3b8' }}>{r.row}</td>
                                        <td style={{ fontSize: 13 }}>{r.title || <span style={{ color: '#b91c1c' }}>—</span>}</td>
                                        <td style={{ fontSize: 12 }}>{r.frequency?.replace('_', ' ')}</td>
                                        <td style={{ fontSize: 12 }}>{r.start_date || <span style={{ color: '#b91c1c' }}>—</span>}</td>
                                        <td style={{ fontSize: 12 }}>{r.assigned_to_name || <span style={{ color: '#94a3b8' }}>{t('maintenance.unassigned', 'Unassigned')}</span>}</td>
                                        <td style={{ fontSize: 11, color: '#b45309' }}>{r.warnings?.join('; ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                        <button type="button" className="btn btn-light" onClick={() => { setShowImportModal(false); setImportPreview(null); }}>{t('maintenance.cancel', 'Cancel')}</button>
                        <button type="button" className="btn btn-primary" disabled={importing || !importPreview.valid_count} onClick={confirmImport}>
                            {importing ? <><span className="spinner-border spinner-border-sm me-2" />{t('maintenance.importing2', 'Importing...')}</> : t('maintenance.importCount', 'Import {n} schedule(s)', { n: importPreview.valid_count })}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
